import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { texts } from 'utils/languages/fr';
import { ActionButton, InfoButton } from 'components';
import { Icon, Box, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const labelSx = {
    display: 'block',
    flex: '1 0',
    marginBottom: '20px',
};

const boxSx = theme => ({
    notStarted: {
        background: '#F8F8F8 0% 0% no-repeat padding-box',
        display: 'flex',
        alignItems: 'center',
    },
    started: {
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        border: `1px solid ${theme.palette.grey['A700']}`,
    },
    completed: {
        background: `${theme.palette.secondary.main} 0% 0% no-repeat padding-box`,
        border: `1px solid ${theme.palette.primary.main}`,
        borderLeft: `6px solid ${theme.palette.primary.main}`,
    },
});

const FormBox = props => {
    const {
        label,
        largeLabel,
        completedLabel,
        children,
        infoTitle,
        infoContent,
        status,
        formId,
        isLast = false,
        handleFormBox,
        handleEditFormBox,
        showInfoButton = true,
    } = props;
    const thisRef = useRef(null);

    useEffect(() => {
        if (status === 'started') {
            setTimeout(() => {
                if (thisRef.current) {
                    thisRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }
            }, 10);
        }
    }, [status]);

    return (
        <>
            <Box
                ref={thisRef}
                sx={theme => ({
                    maxWidth: '660px',
                    marginBottom: '15px',
                    borderRadius: '6px',
                    padding: '30px',
                    position: 'relative',
                    ...boxSx(theme)[status],
                })}
            >
                {status === 'notStarted' && (
                    <Typography
                        sx={theme => ({
                            ...labelSx,
                            color: theme.palette.common.black,
                            fontSize: largeLabel ? '20px' : '16px',
                        })}
                    >
                        {label}
                    </Typography>
                )}
                {status === 'started' && (
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography
                            sx={theme => ({
                                ...labelSx,
                                color: theme.palette.common.black,
                                fontSize: largeLabel ? '20px' : '16px',
                            })}
                        >
                            {label}
                        </Typography>
                        {showInfoButton && (
                            <InfoButton
                                disableMarginBottom
                                title={infoTitle}
                                content={infoContent}
                            />
                        )}
                    </Box>
                )}
                {status === 'completed' && (
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography
                            sx={theme => ({
                                maxWidth: '480px',
                                color: theme.palette.common.black,
                                fontSize: largeLabel ? '20px' : '16px',
                                margin: 0,
                            })}
                        >
                            {completedLabel}
                        </Typography>
                        <Icon
                            id={formId}
                            sx={{
                                right: '30px',
                                top: '30px',
                                fontSize: '35px',
                                cursor: 'pointer',
                                marginLeft: '15px',
                            }}
                            component={icons.PencilCircleOutline}
                            onClick={handleEditFormBox}
                            color="primary"
                        />
                    </Box>
                )}
                {status === 'started' && children}
            </Box>
            {status === 'started' && !isLast && (
                <Box textAlign="right" mb={2}>
                    <ActionButton
                        id={formId}
                        color="primary"
                        sx={{
                            width: 'fit-content',
                            marginLeft: 'auto',
                            padding: 0,
                            fontSize: '16px',
                            letterSpacing: 0,
                        }}
                        onClick={handleFormBox}
                    >
                        {texts.NEXT_QUESTION}{' '}
                        <icons.ArrowDown sx={{ width: '14px', marginLeft: '5px' }} />
                    </ActionButton>
                </Box>
            )}
        </>
    );
};

FormBox.propTypes = {
    label: PropTypes.string.isRequired,
    completedLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    infoTitle: PropTypes.string,
    infoContent: PropTypes.node,
    isLast: PropTypes.bool,
    formId: PropTypes.number,
    showInfoButton: PropTypes.bool,
    largeLabel: PropTypes.bool,
    handleFormBox: PropTypes.func.isRequired,
    handleEditFormBox: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    status: PropTypes.string.isRequired,
};

export default FormBox;
